// Analysis-window resolution for Motion Capture.
//
// Why this exists: the capture canvases used to start playback from wherever
// the playhead happened to be. Dragging a trim handle — or just clicking the
// track — scrubs the video (see TrimSlider's onScrubTo), so a coach who had
// just set the window on the timeline pressed Start with the playhead parked
// on a handle, usually the END one. Playback then hit the end of the window
// almost immediately, the auto-stop fired, fewer than two pose frames were
// captured, and `duration_s = frames.length > 1 ? last.t - first.t : 0`
// saved the session as "0s duration" with an empty report.
//
// The rule this encodes: an analysis always begins at the START of its window,
// and a window that can't contain meaningful footage is refused up front with
// a reason the coach can act on — instead of silently producing an empty
// session that pollutes the roster.

export const MIN_WINDOW_SECONDS = 0.3;

/**
 * Resolve the analysis window for a capture run.
 *
 * @param {{duration?: number, trimStart?: number, trimEnd?: number}} input
 *   `duration` is the media element's duration (may be 0/NaN/Infinity before
 *   or without metadata); `trimEnd` is the trim slider's right handle.
 * @returns {{ok: boolean, error: string|null, start: number, end: number|null}}
 */
export function resolveCaptureWindow({ duration, trimStart, trimEnd } = {}) {
  const dur = Number.isFinite(duration) && duration > 0 ? duration : null;

  let start = Number.isFinite(trimStart) && trimStart > 0 ? trimStart : 0;
  if (dur !== null) start = Math.min(start, dur);

  // Prefer an explicit trim end; otherwise analyse to the end of the clip.
  let end = Number.isFinite(trimEnd) && trimEnd > 0 ? trimEnd : dur;
  if (end === null) {
    return {
      ok: false,
      error:
        "This clip's duration isn't known yet — let it finish loading, then press Start again. If it never loads, re-export the video as H.264 MP4.",
      start,
      end: null,
    };
  }
  if (dur !== null) end = Math.min(end, dur);

  if (end <= start) {
    return {
      ok: false,
      error:
        "The trim window is empty — drag the right handle to the right of the left handle, then press Start.",
      start,
      end,
    };
  }
  if (end - start < MIN_WINDOW_SECONDS) {
    return {
      ok: false,
      error: `The trim window is only ${(end - start).toFixed(2)}s — select at least ${MIN_WINDOW_SECONDS}s of footage to analyse.`,
      start,
      end,
    };
  }
  return { ok: true, error: null, start, end };
}

/** True when the playhead needs to move to reach the window start. */
export function needsRewind(currentTime, start, tolerance = 0.05) {
  if (!Number.isFinite(currentTime)) return true;
  return Math.abs(currentTime - start) > tolerance;
}

/**
 * Convert a SESSION timestamp into an absolute VIDEO timestamp.
 *
 * These are two different timebases and mixing them is a real bug, not a
 * rounding detail:
 *   - Pose frame timestamps (`frames[i].t`) are measured from the moment the
 *     coach pressed Start, so a rep at 2s means "2 seconds into the analysis".
 *   - `video.currentTime` is an offset into the source FILE.
 *
 * The session begins at the window start, so the two differ by `trimStart`.
 * Seeking to the session time directly pulled every keyframe from that many
 * seconds earlier in the UNTRIMMED video — which is why "Visual evidence"
 * screenshots showed footage the clip never contained (2026-09, a transition-zone
 * clip where the athlete was nowhere near the kitchen line, yet the keyframes
 * showed her there).
 *
 * Clamped to the analysis window, then to the clip, so a keyframe can never be
 * grabbed from outside the footage that was actually analysed.
 *
 * @param {number} sessionSeconds  time since Start (frame/rep timebase)
 * @param {{trimStart?: number, trimEnd?: number, duration?: number}} window
 * @returns {number} absolute video time in seconds
 */
export function sessionTimeToVideoTime(sessionSeconds, { trimStart = 0, trimEnd = null, duration = null } = {}) {
  const start = Number.isFinite(trimStart) && trimStart > 0 ? trimStart : 0;
  const session = Number.isFinite(sessionSeconds) ? Math.max(0, sessionSeconds) : 0;
  let t = start + session;

  const dur = Number.isFinite(duration) && duration > 0 ? duration : null;
  const windowEnd = Number.isFinite(trimEnd) && trimEnd > 0 ? trimEnd : dur;
  if (windowEnd !== null) t = Math.min(t, windowEnd);

  // Never seek to (or past) the very end: a video seeked to exactly `duration`
  // yields no decodable frame, which is how you get a black keyframe.
  if (dur !== null) t = Math.min(t, Math.max(0, dur - 0.05));
  return Math.max(0, t);
}
